/**
 * マニュアルサイドバーコンポーネント
 *
 * マニュアル目次のナビゲーション表示
 */

import { useState } from "react";
import { Paper, Stack, TextInput, ScrollArea, Text, NavLink } from "@mantine/core";
import { Search, FileText, Folder, ChevronRight } from "lucide-react";
import { ManualItem, MANUAL_STRUCTURE } from "../../constants/manualStructure";

interface ManualSidebarProps {
  selectedPath: string | null;
  onSelectPath: (path: string | null) => void;
}

/**
 * マニュアルサイドバー
 */
export function ManualSidebar({ selectedPath, onSelectPath }: ManualSidebarProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // ナビゲーションツリーをレンダリング
  const renderNavItems = (items: ManualItem[], level = 0) => {
    return items.map((item) => {
      const hasChildren = item.children && item.children.length > 0;
      const isFile = item.path.endsWith(".md");

      return (
        <NavLink
          key={item.id}
          label={item.title}
          leftSection={isFile ? <FileText size={16} /> : <Folder size={16} />}
          rightSection={hasChildren ? <ChevronRight size={14} /> : null}
          active={selectedPath === item.path}
          onClick={() => {
            if (isFile) {
              onSelectPath(item.path);
            }
          }}
          childrenOffset={16}
          defaultOpened={level === 0}
          style={{ borderRadius: "6px" }}
        >
          {hasChildren && renderNavItems(item.children!, level + 1)}
        </NavLink>
      );
    });
  };

  // 検索でフィルタリング（簡易実装）
  const filteredStructure = searchQuery
    ? MANUAL_STRUCTURE.filter(
        (item) =>
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.children?.some((child) =>
            child.title.toLowerCase().includes(searchQuery.toLowerCase())
          )
      )
    : MANUAL_STRUCTURE;

  return (
    <Paper
      shadow="xs"
      p="md"
      radius="md"
      withBorder
      style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      <Stack gap="sm" style={{ flex: "0 0 auto" }}>
        <TextInput
          placeholder="マニュアル内を検索..."
          leftSection={<Search size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Text size="xs" c="dimmed" fw={500}>
          目次
        </Text>
      </Stack>
      <ScrollArea style={{ flex: 1, marginTop: "10px" }} offsetScrollbars scrollbars="y">
        <Stack gap={4} pr="xs">
          {renderNavItems(filteredStructure)}
        </Stack>
      </ScrollArea>
    </Paper>
  );
}
